import { z } from 'zod';

/**
 * Wire contract for the saved-components feature (Phase 4.5 →
 * Phase 8 server-side swap).
 *
 * The shape mirrors `packages/flow-builder/src/lib/components/types.ts`
 * verbatim — the frontend writes the same blob it consumes, the
 * server treats the payload as opaque JSONB. This keeps the
 * "lift the store off sessionStorage, drop it onto the API"
 * delta minimal: a new fetch wrapper replaces the sessionStorage
 * helper, every downstream consumer (picker, editor, instantiator)
 * is unchanged.
 *
 * The `payload` indirection is intentional. We deliberately do NOT
 * promote individual fields (`name`, `tags`, `containedTypes`, …)
 * into per-column Zod schemas because the frontend's Component
 * type is rich, evolves with every no-code release, and is the
 * authoritative consumer. A future PR can normalise hot keys into
 * top-level columns once we have a real query pattern justifying
 * it.
 *
 * The wire envelope (`projectId`, `id`, `payload`) is the API
 * gateway. The blob is structurally typed and `passthrough()`-ed
 * so a frontend that adds a new optional `Component` field (e.g.
 * `lastRunStatus`) can ship before the backend re-rolls.
 */

export const ComponentPayloadSchema = z
  .object({
    id: z.string().min(1),
    name: z.string().min(1),
    description: z.string().default(''),
    tags: z.array(z.string()).default([]),
    steps: z.array(z.unknown()).default([]),
    icon: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
    projectId: z.string().min(1),
    containedTypes: z.array(z.string()).default([]),
    /**
     * Phase 8.5 · `defer-component-versioning` (Migration 080) — the
     * currently-active version number of this component. Optional on
     * the wire so legacy / pre-080 payloads still validate. When the
     * field is missing the consumer assumes `1`.
     */
    activeVersionNumber: z.number().int().min(1).optional(),
    /**
     * Phase 8.5 · `defer-component-versioning` (Migration 080) — the
     * highest version number that has been saved (active or staging).
     * Always >= `activeVersionNumber`. Optional for the same legacy
     * reason as above.
     */
    latestVersionNumber: z.number().int().min(1).optional(),
  })
  .passthrough();
export type ComponentPayload = z.infer<typeof ComponentPayloadSchema>;

export const ComponentListResponseSchema = z.object({
  items: z.array(ComponentPayloadSchema),
});
export type ComponentListResponse = z.infer<typeof ComponentListResponseSchema>;

export const ComponentUpsertRequestSchema = ComponentPayloadSchema;
export type ComponentUpsertRequest = z.infer<typeof ComponentUpsertRequestSchema>;

/**
 * Response shape for `PUT /api/components`. The server now reports
 * whether this save landed as the new active version (first save of
 * a fresh id) or as a staging version (subsequent saves of an
 * existing id). `versionNumber` is always the number of the row that
 * was just persisted. `isStaging = true` means the caller still needs
 * to call `POST /api/components/:id/versions/:n/promote` for the
 * payload to become the active version.
 */
export const ComponentUpsertResponseSchema = z.object({
  component: ComponentPayloadSchema,
  versionNumber: z.number().int().min(1),
  isStaging: z.boolean(),
});
export type ComponentUpsertResponse = z.infer<typeof ComponentUpsertResponseSchema>;

export const ComponentDeleteResponseSchema = z.object({
  deleted: z.boolean(),
});
export type ComponentDeleteResponse = z.infer<typeof ComponentDeleteResponseSchema>;

/**
 * Phase 8.5 · `defer-component-versioning` — one saved version of a
 * component. The `payload` field carries the same shape as
 * `ComponentPayloadSchema` (passed through as `unknown` so the
 * version envelope doesn't repeat the schema; callers re-parse).
 */
export const ComponentVersionSchema = z.object({
  id: z.string().min(1),
  componentId: z.string().min(1),
  versionNumber: z.number().int().min(1),
  payload: z.unknown(),
  createdAt: z.string(),
  createdBy: z.string().optional(),
});
export type ComponentVersion = z.infer<typeof ComponentVersionSchema>;

/** `GET /api/components/:id/versions` — full version list + active pointer. */
export const ComponentVersionListResponseSchema = z.object({
  versions: z.array(ComponentVersionSchema),
  activeVersionNumber: z.number().int().min(1),
  latestVersionNumber: z.number().int().min(1),
});
export type ComponentVersionListResponse = z.infer<typeof ComponentVersionListResponseSchema>;

/** `POST /api/components/:id/versions/:version/promote` — empty request body, returns updated component. */
export const PromoteComponentVersionResponseSchema = z.object({
  component: ComponentPayloadSchema,
  promotedVersionNumber: z.number().int().min(1),
});
export type PromoteComponentVersionResponse = z.infer<typeof PromoteComponentVersionResponseSchema>;
